import React, { useState } from 'react';
import './projects.css';

const Projects = () => {
  const projectsData = [
    {
      id: 1,
      title: "Hemoglobin Strip Manufacturing Machine",
      description:
        "We developed a fully automated machine for manufacturing hemoglobin strips. The system is designed to precisely layer three components—membrane, mesh, and label—and cut them with an accuracy of ±0.05 mm. By automating the entire process, the machine eliminates manual intervention, ensuring high consistency, reliability, and productivity in strip manufacturing.",
      youtubeIds: ["oRoOSAvEB-4"], // Single video as array
      category: "manufacturing",
    },
    {
      id: 2,
      title: "Hemoglobin Strip Punching Automation",
      description:
        "We automated the punching process of hemoglobin strips by integrating the system with power press machines. This solution enables fully unattended operation, making the shop floor virtually manless. The automation not only enhances productivity and precision but also minimizes human error, ensuring consistent output and improved efficiency.",
      youtubeIds: ["RolLMXpq64c"],
      category: "automation",
    },
    {
      id: 3,
      title: "Aluminium Profile Buffing Machine",
      description:
        "We developed a buffing machine specifically designed to remove burrs from aluminium profiles used in car rooftops—an essential automotive component. After machining, these profiles often contain small burrs that were traditionally removed manually through hand deburring. Our automated buffing machine eliminates the need for manual work, ensuring faster, more consistent, and high-quality finishing, while significantly improving productivity and reducing operator fatigue.",
      youtubeIds: ["swmHzaUVjS0"],
      category: "automotive",
    },
    {
      id: 4,
      title: "Mini CNC Chamfering Machine",
      description:
        "We developed a compact CNC machine equipped with a Hust controller, designed for precision chamfering of automobile parts. The machine performs 0.5 × 45° chamfers with micron-level accuracy, delivering smooth and reliable operation. This solution ensures high repeatability, reduces dependency on manual machining, and enhances the overall quality of automotive components.",
      youtubeIds: ["ZJD-sjWmbRA", "dPXWaRK9SlQ"], // Two videos for this project
      category: "cnc",
    },
    {
      id: 5,
      title: "Himalaya Baby Lotion Cap Assembly Machine",
      description:
        "We developed a fully automated cap assembly machine for Himalaya Baby Lotion, capable of assembling four components—cartridge, closure, actuator, and dip tube—without manual intervention. The system operates at a speed of 35 parts per minute (PPM), ensuring high throughput and consistent quality. Components are precisely fed through bowl feeders and seamlessly assembled within the machine, significantly improving efficiency and reducing labor dependency.",
      youtubeIds: ["mcAZL2xqGoI"],
      category: "assembly",
    },
    {
      id: 6,
      title: "Aerosol Valve Assembly Machine",
      description:
        "The aerosol valve assembly machine is designed to accurately assemble five critical components and perform precise crimping without any leakage, ensuring reliable performance across all types of spray applications. The machine assembles the stem, gasket, ferrule, housing, and spring in a controlled sequence. All components are precisely aligned and securely crimped at a dedicated station, resulting in consistent quality, airtight sealing, and high production efficiency.",
      youtubeIds: ["YAUi3URR60c"],
      category: "assembly",
    },
    {
      id: 7,
      title: "Custom PCB and Firmware Development",
      description:
        "We design and develop our own custom PCBs and firmware tailored for both domestic and industrial applications. Our expertise covers a wide range of solutions, including: Commercial Food Oven PCBs – ensuring reliable performance and precise control for high-temperature applications. Industrial IoT Devices – supporting multiple communication protocols such as Wi-Fi, Bluetooth, I²C, SPI, RS485, and more, enabling seamless connectivity and integration. By combining hardware design with firmware development, we deliver optimized, reliable, and scalable solutions for diverse industry needs.",
      youtubeIds: ["CF3SoOnr3Q0"],
      category: "electronics",
    },
    {
      id: 8,
      title: "Industry 4.0 IIoT Software",
      description:
        "We have developed our own Industry 4.0–enabled IIoT software to monitor machine performance and energy consumption in real time. The platform provides: Live data monitoring with trends and graphical visualization, Energy tracking for efficiency analysis, Alerts and notifications for critical events, Historical data logging for performance review and optimization, Remote accessibility for anytime, anywhere monitoring. This solution empowers industries with actionable insights, improved productivity, and smarter decision-making through digital transformation.",
      youtubeIds: ["y512PZ1r0j8"],
      category: "software",
    },
    {
      id: 9,
      title: "Robot",
      description:
        "We have developed an advanced robotic system capable of performing high-precision machining operations on missile and aerospace components. The robot is engineered to execute multiple complex tasks, including drilling, milling, and angled drilling on internal diameters (ID). Its multi-axis configuration and automated control ensure exceptional accuracy, repeatability, and surface finish, meeting the stringent requirements of the aerospace and defence sectors.",
      youtubeIds: ["0hafhb8B3IY"],
      category: "Defence",
    },
    {
      id: 10,
      title: "Filament Winding Machine",
      description:
        "Our filament winding machine is designed to manufacture lightweight aerospace and defence bodies, enabling superior structural strength while reducing overall weight for improved flight performance. The system is fully integrated with CNC control, providing a user-friendly interface and precise axis movement tailored to the specific job requirements. This ensures consistent winding patterns, high repeatability, and optimal material utilization for mission-critical components.",
      youtubeIds: ["_79oTfhswtM"],
      category: "Defence",
    },
    {
      id: 11,
      title: "Renewable Energy",
      description:
        "We are designing advanced wind turbines that generate renewable energy in an eco-friendly and sustainable manner. Our turbine systems are engineered for high efficiency, reduced environmental impact, and reliable power generation, supporting the transition toward clean and green energy solutions.",
      youtubeIds: ["iBBrNFbmESI"],
      category: "Renewable-Energy",
      // 🔽 UPDATE YOUR IMAGE PATHS HERE
      galleryImages: [
        "/images/RenewableEnergy1.jpeg",
        "/images/RenewableEnergy2.jpeg",
        "/images/RenewableEnergy3.jpeg",
        "/images/RenewableEnergy4.jpeg"
      ],
    },
  ];

  const categories = [
    "all",
    "manufacturing",
    "automation",
    "automotive",
    "cnc",
    "assembly",
    "electronics",
    "software",
    "Defence",
    "Renewable-Energy",
  ];

  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProjects = projectsData.filter((project) => {
    const matchesCategory =
      activeCategory === "all" || project.category === activeCategory;
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // 🔹 Image Gallery Component (always stacked below video)
  const ImageGallery = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!images || images.length === 0) return null;

    const goToPrev = () => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goToNext = () => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const goToIndex = (index) => {
      setCurrentIndex(index);
    };

    return (
      <div className="image-gallery">
        <div className="image-gallery-main">
          <button
            type="button"
            className="gallery-arrow gallery-arrow-left"
            onClick={goToPrev}
            aria-label="Previous image"
          >
            ‹
          </button>

          <div className="image-gallery-image-wrapper">
            <img
              src={images[currentIndex]}
              alt={`Renewable Energy image ${currentIndex + 1}`}
              className="image-gallery-image"
            />
          </div>

          <button
            type="button"
            className="gallery-arrow gallery-arrow-right"
            onClick={goToNext}
            aria-label="Next image"
          >
            ›
          </button>
        </div>

        <div className="image-gallery-dots">
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`gallery-dot ${index === currentIndex ? "active" : ""}`}
              onClick={() => goToIndex(index)}
              aria-label={`Show image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  };

  // 🔹 Video + (optional) Gallery in SAME COLUMN
  const ProjectMedia = ({ youtubeIds, title, galleryImages }) => {
    // Root div has .project-video => this is one flex column in your layout
    if (!youtubeIds || youtubeIds.length === 0) return null;

    // Single video
    if (youtubeIds.length === 1) {
      return (
        <div className="project-video video-section">
          <div className="video-wrapper video-fixed-aspect">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${youtubeIds[0]}`}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          {/* 🔽 Gallery is ALWAYS BELOW video, same width */}
          {galleryImages && galleryImages.length > 0 && (
            <ImageGallery images={galleryImages} />
          )}
        </div>
      );
    }

    // Two videos stacked + gallery below (if any)
    if (youtubeIds.length === 2) {
      return (
        <div className="project-video double-video-section">
          <div className="video-stack">
            <div className="video-wrapper video-fixed-aspect">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${youtubeIds[0]}`}
                title={`${title} - Video 1`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="video-wrapper video-fixed-aspect">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${youtubeIds[1]}`}
                title={`${title} - Video 2`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          {galleryImages && galleryImages.length > 0 && (
            <ImageGallery images={galleryImages} />
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="projects-page">
      <div className="projects-header">
        <h1>Our Projects</h1>
        <p>Innovative solutions driving industrial automation and digital transformation</p>
      </div>

      {/* Filters and Search */}
      <div className="projects-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="category-filters">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${
                activeCategory === category ? "active" : ""
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Projects List */}
      <div className="projects-list">
        {filteredProjects.map((project) => (
          <div
            key={project.id}
            className={`project-item ${
              project.youtubeIds.length === 2
                ? "project-item-double-video"
                : "project-item-equal"
            }`}
          >
            {/* LEFT COLUMN: video + gallery (for Renewable Energy) */}
            <ProjectMedia
              youtubeIds={project.youtubeIds}
              title={project.title}
              galleryImages={project.galleryImages}
            />

            {/* RIGHT COLUMN: content */}
            <div className="project-content content-section">
              <div className="project-header">
                <span className={`category-tag ${project.category}`}>
                  {project.category}
                </span>
                <h2>{project.title}</h2>
              </div>
              <div className="project-description text-container">
                <p>{project.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="no-projects">
          <h3>No projects found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default Projects;
